// Auth Service - Contract-First Enterprise Approach (SDK types as source of truth)
import type { 
  AuthRequest,
  AuthResponse,
  UserResponse,
  UserRegistrationRequest
} from '@suvadi/api-client';
import { AuthenticationApi, Configuration } from '@suvadi/api-client';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/suvadi';

class AuthService {
  // Base configuration for SDK with authentication token (when available)
  private getAuthApi(): AuthenticationApi {
    const config = new Configuration({
      basePath: API_BASE_URL,
      accessToken: () => {
        const token = localStorage.getItem('token');
        console.log('Auth SDK getting token:', token ? `Token present (${token.substring(0, 20)}...)` : 'No token');
        return token || '';
      }
    });
    return new AuthenticationApi(config);
  }

  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  async login(credentials: AuthRequest): Promise<AuthResponse> {
    try {
      const authApi = this.getAuthApi();
      const response = await authApi.login({
        email: credentials.email,
        password: credentials.password,
      });

      // SDK returns AuthResponse: { token?, error? }
      const authData: AuthResponse = response.data;
      
      if (authData.token) {
        localStorage.setItem('token', authData.token);
        localStorage.setItem('userEmail', credentials.email);

        // Also store user data in localStorage for convenience
        try {
          const userApi = this.getAuthApi();
          const userResponse = await userApi.getCurrentUser();
          const userData: UserResponse = userResponse.data;
          
          if (userData.userId) {
            localStorage.setItem('userId', userData.userId);
          }
        } catch (userError) {
          console.warn('Failed to get user data after login:', userError);
        }

        // Return SDK AuthResponse directly - let components get user data separately
        return authData;
      }

      throw new Error(authData.error || 'Login failed');
    } catch (error: unknown) {
      const apiError = error as { response?: { data?: { error?: string; message?: string } }; message?: string };
      const message = apiError.response?.data?.error || apiError.response?.data?.message || apiError.message || 'Login failed';
      throw new Error(message);
    }
  }

  async register(userData: UserRegistrationRequest): Promise<AuthResponse> {
    try {
      const authApi = this.getAuthApi();
      const response = await authApi.register({
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
        password: userData.password,
      });

      // SDK returns AuthResponse: { token?, error? }
      const authData: AuthResponse = response.data;
      
      if (authData.token) {
        localStorage.setItem('token', authData.token);
        localStorage.setItem('userEmail', userData.email);

        // Also store user data in localStorage for convenience
        try {
          const userApi = this.getAuthApi();
          const userResponse = await userApi.getCurrentUser();
          const userInfo: UserResponse = userResponse.data;
          
          if (userInfo.userId) {
            localStorage.setItem('userId', userInfo.userId);
          }
        } catch (userError) {
          console.warn('Failed to get user data after registration:', userError);
        }

        // Return SDK AuthResponse directly
        return authData;
      }

      throw new Error(authData.error || 'Registration failed');
    } catch (error: unknown) {
      const apiError = error as { response?: { data?: { error?: string; message?: string } }; message?: string };
      const message = apiError.response?.data?.error || apiError.response?.data?.message || apiError.message || 'Registration failed';
      throw new Error(message);
    }
  }

  async logout(): Promise<void> {
    try {
      // TODO: Backend logout endpoint not implemented yet - using manual fetch
      // Will be replaced with SDK once backend adds /api/v1/auth/logout endpoint
      await fetch(`${API_BASE_URL}/api/v1/auth/logout`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
      });
    } catch (error) {
      console.warn('Logout endpoint not available (expected):', error);
    } finally {
      // Always clear local storage for client-side logout
      this.clearAuthData();
    }
  }

  async getCurrentUser(): Promise<UserResponse | null> {
    const token = localStorage.getItem('token');
    if (!token) return null;

    try {
      const authApi = this.getAuthApi();
      const response = await authApi.getCurrentUser();
      
      // Return SDK UserResponse directly
      const userData: UserResponse = response.data;
      return userData;
    } catch (error: unknown) {
      console.error('Failed to get current user:', error);
      // Clear auth data on 401 errors
      const apiError = error as { response?: { status?: number } };
      if (apiError.response?.status === 401) {
        this.clearAuthData();
      }
      return null;
    }
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem('token');
  }

  getToken(): string | null {
    return localStorage.getItem('token');
  }

  getUserId(): string | null {
    return localStorage.getItem('userId');
  }

  getUserEmail(): string | null {
    return localStorage.getItem('userEmail');
  }

  clearAuthData(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('userEmail');
  }

  async refreshToken(): Promise<boolean> {
    try {
      // TODO: Backend refresh endpoint not implemented yet - using manual fetch  
      // Will be replaced with SDK once backend adds /api/v1/auth/refresh endpoint
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.token) {
          localStorage.setItem('token', data.token);
          return true;
        }
      }
    } catch (error) {
      console.warn('Token refresh endpoint not available (expected):', error);
    }

    this.clearAuthData();
    return false;
  }
}

export const authService = new AuthService();

// Export only SDK types - no more frontend compatibility types needed!
export type { AuthRequest, UserRegistrationRequest, UserResponse, AuthResponse };
